<template>
    <div class="flex overflow-auto p-30" :style="{'background-color': '#ebeff2', 'height': '70vh'}">
        <div class="w-100 text-center flex justify-center align-center" v-if="loading">
            <h3>{{ trans('documents.document_template_choose_heading') }}</h3>
        </div>
        <form class="m-auto">
            <div ref="pdfViewer" id="pdf-viewer">
                <template v-if="!loading">
                    <div v-for="(page, index) in pdf.numPages" :key="index" :ref="'wrapperPage' + page" :id="'wrapperPage' + page">
                        <div v-for="(input, index) in inputs['page_' + page]" :key="index">
                            <template v-if="is_diagnosis_app_active && input.name === 'Diagnose'">
                                <v-select
                                    :id="input.name + page"
                                    :name="input.name"
                                    :style="input.style"
                                    load-from-url="/app/diagnoses"
                                    :options="[{ id: pdfContent[input.vModelName], label: pdfContent[input.vModelName]}]"
                                    :urlParams="{system: 'ICPC2'}"
                                    v-model="pdfContent[input.vModelName]"
                                    valueKey="label"
                                >
                                </v-select>
                            </template>
                            <template v-else-if="input.type !== 'textarea'">
                                <input :id="input.name + input.value + page" class="form-control" :type="input.type" :name="input.name" :style="input.style" v-model="pdfContent[input.vModelName]">
                            </template>
                            <template v-else>
                                <textarea class="form-control" :style="input.style" v-model="pdfContent[input.name]"></textarea>
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </form>
    </div>
</template>

<script type="text/javascript">
import Apps from '@/core/Apps'
import VSelect from '@/system/components/global/form/VSelect';

export default {
    components: {
        VSelect
    },

    props: {
        template: {
            type: [Object, Number],
            required: false,
        },

        isEditing: {
            type: Boolean,
            required: false,
            default: false,
        },

        client: {
            type: Object,
            required: false,
        },

        value: {
            type: Object
        },
    },

    data() {
        return {
            loading: true,
            pdf: null,
            inputs: {},
            pdfContent: this.value,
            pdfData: {
                scale: 1.6,
                viewportWidth: 0,
                viewportHeight: 0,
                pages: {},
            },
            tagReplacements: this.client ? this.$store.getters['journals/getTagReplacementsForClientOrGroup'](this.client, false) : [],
            excludedFieldNames: ['undefined', 'nullstill', 'undefined2', 'undefined_3', 'undefined25']
        }
    },

    computed: {
        is_diagnosis_app_active() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Diagnosis);
        },
    },

    methods: {
        renderPage(pageNumber, canvas) {
            let vm = this

            this.pdf.getPage(pageNumber).then(function(page) {
                page.getAnnotations().then(function(annotations) {
                    for (let i = 0; i < annotations.length; i++) {
                        const annotation = annotations[i]

                        if (vm.excludedFieldNames.includes(annotation.fieldName)) {
                            continue
                        }

                        const input = vm.buildInputData(annotation, pageNumber)

                        if (!vm.isEditing) {
                            vm.pdfContent[input.name] = input.vModelValue
                        }

                        vm.pdfData.pages['page_' + pageNumber].push({
                            bottomLeftX: annotation.rect[0],
                            bottomLeftY: annotation.rect[1],
                            topRightX: annotation.rect[2],
                            topRightY: annotation.rect[3],
                            type: input.type,
                            name: input.vModelName,
                            value: input.value,
                        })

                        vm.inputs['page_' + pageNumber].push(input)
                    }
                })

                const viewport = page.getViewport({scale: vm.pdfData.scale})

                canvas.width = vm.pdfData.viewportWidth = viewport.width
                canvas.height = vm.pdfData.viewportHeight = viewport.height

                page.render({canvasContext: canvas.getContext('2d'), viewport: viewport})
            })
        },

        buildInputData(annotation, pageNumber) {
            const annotationBottomLeftX = annotation.rect[0] * this.pdfData.scale
            const annotationBottomLeftY = annotation.rect[1] * this.pdfData.scale
            const annotationTopRightX = annotation.rect[2] * this.pdfData.scale
            const annotationTopRightY = annotation.rect[3] * this.pdfData.scale

            let input = {}
            input.type = 'text'
            input.value = inputValue = annotation.fieldValue ? annotation.fieldValue : null

            let tagReplacement = this.tagReplacements.find((tag) => {
                if (tag.newStyleTag === inputValue) {
                    return tag
                }
            })

            input.vModelValue = tagReplacement ? tagReplacement.value : inputValue

            if (Array.isArray(input.vModelValue)) {
                input.vModelValue = null
            }

            input.name = input.vModelName = annotation.fieldName
            input.style = {
                'position': 'absolute',
                'left': annotationBottomLeftX + 'px',
                'bottom': annotationBottomLeftY + 'px',
                'width': annotationTopRightX - annotationBottomLeftX + 'px',
                'height': annotationTopRightY - annotationBottomLeftY + 'px',
                'color': '#000',
            }

            if (annotation.fieldType === 'Tx' && annotation.multiLine) {
                input.type = 'textarea'
                input.style.minHeight = '0px'
                input.style.resize = 'none'
            } else if (annotation.fieldType === 'Btn' && annotation.checkBox) {
                input.type = 'checkbox'
                input.value = annotation.exportValue
                input.vModelValue = null
                input.style.appearance = 'auto'
                input.style.cursor = 'pointer'
            } else if (annotation.fieldType === 'Btn' && annotation.radioButton) {
                if (pageNumber > 1) {
                    input.name = annotation.fieldName + pageNumber
                }

                input.type = 'radio'
                input.value = annotation.buttonValue
                input.vModelValue = null
                input.style.cursor = 'pointer'
            } else {
                input.style.paddingTop = '2px'
                input.style.paddingBottom = '2px'
                input.style.paddingLeft = '2px'
                input.style.paddingRight = '2px'
            }

            return input
        },

        initPdf() {
            let viewer = this.$refs.pdfViewer

            for (page = 1; page <= this.pdf.numPages; page++) {
                this.inputs['page_' + page] = []
                this.pdfData.pages['page_' + page] = []

                if (this.$refs['wrapperPage' + page].length) {
                    // Create the pdf page wrapper
                    let canvasWrapper = this.$refs['wrapperPage' + page][0]
                    canvasWrapper.className = 'pdf-page-wrapper position-relative'

                    // Create the canvas
                    let canvas = document.createElement('canvas')

                    canvasWrapper.appendChild(canvas)
                    viewer.appendChild(canvasWrapper)

                    this.renderPage(page, canvas, canvasWrapper)
                }

            }
        },

        getDocument(template) {
            let vm = this

            pdfjsLib.getDocument('/assets/document-templates/' + template.application_file_path).promise.then(function(pdf) {
                vm.pdf = pdf
                vm.loading = false

                // Have to set a minimal timeout here otherwise the dom won't be updated in time.
                // Surely there must be a better way...
                setTimeout(() => {
                    vm.initPdf()
                }, 1)
            })
        },

        resetDocument() {
            this.pdf = null
            this.inputs = {}
            this.pdfData = {
                content: {},
                scale: 1.6,
                viewportWidth: 0,
                viewportHeight: 0,
                pages: {},
            }
        },
    },

    watch: {
        pdfContent(val) {
            this.$emit('input', val);
        },

        pdfData: {
            deep: true,
            handler: function(val) {
                this.$emit('updatedPdfData', val)
            },
        },

        template: {
            deep: true,
            handler: function(val) {
                this.getDocument(val)
            }
        }
    },

    mounted() {
        if (this.template) {
            this.getDocument(this.template)
        }
    },

    beforeUnmount() {
        this.resetDocument()
    }
}
</script>
